import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api, errMsg } from "@/lib/api";
import { PasswordInput, ConsentBoxes } from "@/components/AuthFields";

const input = "w-full h-11 px-3 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-yellow/70";

export default function Login() {
  const [params] = useSearchParams();
  const next = params.get("next") || "/";
  const [mode, setMode] = useState(params.get("registrati") ? "register" : "login");
  const [f, setF] = useState({ name: "", email: params.get("email") || "", password: "", password_confirm: "", privacy_accepted: false, marketing_consent: false, profiling_consent: false, phone: "", is_company: false, company: "", vat: "", sdi: "", address: "", city: "", zip: "", province: "", extra_notes: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: f.email });
        toast.success("Se l'email è registrata riceverai un link per impostare la password");
        setMode("login");
      } else {
        if (mode === "register" && f.password !== f.password_confirm) throw new Error("Le due password non coincidono");
        if (mode === "register" && !f.privacy_accepted) throw new Error("Per registrarti devi dichiarare di aver letto l'informativa privacy");
        const u = mode === "login" ? await login(f.email, f.password) : await register(f);
        navigate(next === "/" && u.role === "admin" ? "/admin" : next === "/" && u.role === "manager" ? "/manager" : next);
      }
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <h1 className="text-5xl font-extrabold uppercase mb-2">{mode === "login" ? "Accedi" : mode === "register" ? "Crea account" : "Password dimenticata"}</h1>
      <p className="text-sm text-slate-600 mb-8">
        {mode === "forgot" ? "Ti invieremo un link per impostare una nuova password." : "Eri cliente di halfatimbri.it? Il tuo account è già attivo: accedi con la stessa email e password."}
      </p>
      {mode !== "forgot" && (
        <div className="grid grid-cols-2 mb-6 rounded-md border border-slate-200 p-1 bg-white">
          {[["login", "Accedi"], ["register", "Registrati"]].map(([m, l]) => (
            <button key={m} data-testid={`auth-tab-${m}`} onClick={() => setMode(m)} className={`press h-9 rounded text-sm font-semibold ${mode === m ? "bg-brand-navy text-white" : ""}`}>{l}</button>
          ))}
        </div>
      )}
      <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
        {mode === "register" && <input data-testid="auth-name-input" className={input} placeholder="Nome e cognome" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />}
        <input data-testid="auth-email-input" className={input} type="email" placeholder="Email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        {mode !== "forgot" && <PasswordInput testid="auth-password-input" className={input} placeholder="Password (min. 6 caratteri)" required minLength={6} autoComplete={mode === "login" ? "current-password" : "new-password"} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />}
        {mode === "register" && (
          <div>
            <PasswordInput testid="auth-password-confirm-input" className={input} placeholder="Ripeti la password" required minLength={6} autoComplete="new-password" value={f.password_confirm} onChange={(e) => setF({ ...f, password_confirm: e.target.value })} />
            {f.password_confirm && f.password !== f.password_confirm && <p className="text-xs text-rose-700 mt-1" data-testid="auth-password-mismatch">Le password non coincidono</p>}
          </div>
        )}
        {mode === "register" && <CompanyFields f={f} setF={setF} />}
        {mode === "register" && <ConsentBoxes f={f} setF={setF} />}
        {params.get("confermato") && mode === "login" && <div data-testid="auth-confirmed-note" className="rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-900">Iscrizione confermata. Accedi con la <b>password temporanea</b> che trovi nell'email: subito dopo ne sceglierai una nuova.</div>}
        {err && <div className="text-sm text-rose-700 font-medium" data-testid="auth-error">{err}</div>}
        <button data-testid="auth-submit-btn" disabled={busy} className="press w-full h-11 rounded-md bg-brand-yellow text-brand-navy font-semibold flex items-center justify-center gap-2 hover:bg-brand-yellowdark disabled:opacity-60">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} {mode === "login" ? "Accedi" : mode === "register" ? "Crea account" : "Invia link"}
        </button>
        {mode !== "register" && <button type="button" data-testid="auth-forgot-link" onClick={() => setMode(mode === "forgot" ? "login" : "forgot")} className="text-sm text-slate-600 hover:text-brand-navy">
          {mode === "forgot" ? "← Torna all'accesso" : "Password dimenticata?"}
        </button>}
      </form>
    </div>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const [pw, setPw] = useState("");
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/reset-password", { token: params.get("token"), password: pw });
      setDone(true);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div className="max-w-md mx-auto px-4 py-16 space-y-6">
      <h1 className="text-5xl font-extrabold uppercase">Nuova password</h1>
      {done ? (
        <p data-testid="reset-done">Password aggiornata. <Link to="/accedi" className="text-brand-navy font-semibold">Accedi</Link></p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <PasswordInput testid="reset-password-input" minLength={6} required className={input} placeholder="Nuova password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} />
          <button data-testid="reset-submit-btn" className="press w-full h-11 rounded-md bg-brand-yellow text-brand-navy font-semibold">Salva password</button>
        </form>
      )}
    </div>
  );
}

const BILL = [["company", "Ragione sociale *", "sm:col-span-2", 160], ["vat", "Partita IVA *", "", 16], ["sdi", "Codice destinatario SDI *", "", 7],
  ["address", "Via e numero civico *", "sm:col-span-2", 200], ["city", "Città *", "", 100], ["zip", "CAP *", "", 5], ["province", "Provincia (es. FC) *", "", 2],
  ["phone", "Telefono", "", 40]];

function CompanyFields({ f, setF }) {
  const cls = "w-full h-11 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-yellow/70";
  return (
    <div className="space-y-3">
      <label className="flex items-center gap-3 rounded-md border border-slate-200 bg-white p-3 cursor-pointer" data-testid="auth-company-toggle-label">
        <input type="checkbox" data-testid="auth-is-company-checkbox" checked={f.is_company} onChange={(e) => setF({ ...f, is_company: e.target.checked })} className="w-4 h-4 accent-brand-navy" />
        <span className="text-sm font-semibold">Sono un'azienda / ho la Partita IVA</span>
      </label>
      {f.is_company && (
        <div className="rounded-md border-2 border-brand-navy bg-white p-4 space-y-3 fade-up" data-testid="auth-company-fields">
          <div className="font-mono text-[11px] tracking-widest text-slate-500">DATI DI FATTURAZIONE</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {BILL.map(([k, ph, span, max]) => (
              <input key={k} data-testid={`auth-${k}-input`} className={`${cls} ${span}`} placeholder={ph} maxLength={max} required={ph.endsWith("*")}
                value={f[k]} onChange={(e) => setF({ ...f, [k]: k === "sdi" || k === "province" ? e.target.value.toUpperCase() : e.target.value })} />
            ))}
            <textarea data-testid="auth-extra-notes-input" rows={3} maxLength={1000} className={`${cls} h-auto py-2 sm:col-span-2`}
              placeholder="Note aggiuntive: insegna del negozio, ufficio, piano, citofono…" value={f.extra_notes} onChange={(e) => setF({ ...f, extra_notes: e.target.value })} />
          </div>
          <p className="text-xs text-slate-500">Non hai il codice SDI? Scrivi 0000000 e la fattura arriverà nel tuo cassetto fiscale.</p>
        </div>
      )}
    </div>
  );
}
